import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Mail, Trash2, Crown } from 'lucide-react';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const JointAccountDialog = ({ open, onClose, calendar }) => {
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);

  useEffect(() => {
    if (open && calendar) {
      loadMembers();
    }
  }, [open, calendar]);

  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await axios.get(
        `${API}/calendars/${calendar.id}/members`,
        { withCredentials: true }
      );
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Erreur lors du chargement des membres');
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Veuillez entrer un email');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API}/calendars/${calendar.id}/invite`,
        { email: inviteEmail },
        { withCredentials: true }
      );
      
      toast.success('Invitation envoyée ! L\'utilisateur peut maintenant accéder au calendrier.');
      setInviteEmail('');
      await loadMembers();
    } catch (error) {
      console.error('Error inviting user:', error);
      if (error.response?.status === 404) {
        toast.error('Utilisateur non trouvé. Il doit d\'abord créer un compte.');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Ce calendrier a déjà 2 membres');
      } else {
        toast.error('Erreur lors de l\'invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const canInvite = calendar?.type === 'joint' && members.length < 2;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-600" />
            Gestion du Compte Joint
          </DialogTitle>
          <DialogDescription>
            Partagez ce calendrier avec une autre personne
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Members List */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Membres ({members.length}/2)</h3>
            {loadingMembers ? (
              <div className="text-center py-4 text-gray-500">Chargement...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-4 text-gray-500">Aucun membre</div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-white"
                  >
                    {member.picture ? (
                      <img
                        src={member.picture}
                        alt={member.name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-teal-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{member.name}</div>
                      <div className="text-xs text-gray-500 truncate">{member.email}</div>
                    </div>
                    {member.role === 'owner' && (
                      <Crown className="h-4 w-4 text-amber-500" title="Propriétaire" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invite Section */}
          {canInvite && (
            <div className="space-y-3 pt-4 border-t">
              <h3 className="text-sm font-semibold">Inviter une personne</h3>
              <div>
                <Label htmlFor="invite-email">Email</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="nom@email.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleInvite()}
                      className="pl-10"
                    />
                  </div>
                  <Button
                    onClick={handleInvite}
                    disabled={loading || !inviteEmail.trim()}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {loading ? 'Envoi...' : 'Inviter'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                💡 La personne doit avoir un compte sur Leu Budget du Génie
              </p>
            </div>
          )}

          {!canInvite && calendar?.type === 'joint' && members.length >= 2 && (
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              ✅ Ce calendrier est complet (2 membres maximum)
            </div>
          )}

          {calendar?.type === 'personal' && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              ℹ️ Les comptes personnels ne peuvent pas être partagés. Créez un compte joint pour partager.
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JointAccountDialog;
